import { Effect } from '@babylonjs/core/Materials/effect'
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture'
import { Texture } from '@babylonjs/core/Materials/Textures/texture'
import { Matrix, Vector2, Vector3 } from '@babylonjs/core/Maths/math'
import { PostProcess } from '@babylonjs/core/PostProcesses/postProcess'
import ModelViewer from '../ModelViewer'
import { Settings } from '../Settings'

Effect.ShadersStore['blendShaderFragmentShader'] = String.raw`
#ifdef GL_ES
precision highp float;
#endif

varying vec2 vUV;

uniform sampler2D textureSampler; // camera render (Babylon provides)
uniform sampler2D blendTexture;   // fixed texture
uniform float progress;
uniform vec2 center;
uniform float randomSeed;

vec4 getFromColor(vec2 uv){ return texture2D(blendTexture, uv); }   // start
vec4 getToColor(vec2 uv){ return texture2D(textureSampler, uv); }   // end (scene)

float random(vec2 p){
	return fract(sin(dot(p + randomSeed, vec2(12.9898,78.233))) * 43758.5453123);
}

float noise(vec2 p){
	vec2 i = floor(p);
	vec2 f = fract(p);
	f = f*f*(3.0 - 2.0*f);
	return mix(
		mix(random(i), random(i + vec2(1.0,0.0)), f.x),
		mix(random(i + vec2(0.0,1.0)), random(i + vec2(1.0,1.0)), f.x),
		f.y
	);
}

float fbm(vec2 p){
	float v = 0.0;
	float a = 0.5;
	for (int i = 0; i < 6; i++){
		v += a * noise(p);
		p *= 2.0;
		a *= 0.5;
	}
	return v;
}

vec4 transition(vec2 uv){
	float dist = distance(uv, center);
	float brush = fbm(uv * vec2(20.0, 20.0));
	float offset = 0.15 * (brush - 0.5);
	float edge = progress - (dist - offset * 0.9);
	float mask = smoothstep(-0.05, 0.05, edge);

	if (progress <= 0.0) mask = 0.0;
	if (progress >= 1.0) mask = 1.0;

	return mix(getFromColor(uv), getToColor(uv), mask);
}

void main(void){
	gl_FragColor = transition(vUV);
}
`

let postProcess: PostProcess

let texA: RenderTargetTexture
let center: Vector2
let progress = 0
let _viewer: ModelViewer
let _isTransitioning = false
let filenames: string

export async function prepareTransition(viewer: ModelViewer) {
	_viewer = viewer
	if (viewer.frame.hasErrors) return
	if (viewer.canvas.width == 300 && viewer.canvas.height == 150) {
		return
	}

	texA?.dispose()
	texA = new RenderTargetTexture('TexA', { width: viewer.canvas.width, height: viewer.canvas.height }, viewer.scene)
	texA.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE
	texA.activeCamera = viewer.camera
	texA.renderList = viewer.scene.meshes
	await viewer.scene.whenReadyAsync()
	viewer.scene.updateTransformMatrix(true)

	const { x, y } = Vector3.Project(Vector3.Zero(), Matrix.Identity(), viewer.scene.getTransformMatrix(), viewer.camera.viewport)
	texA.render(true)
	center = new Vector2(Math.min(Math.max(x, 0), 1), Math.min(Math.max(1 - y, 0), 1))
	filenames = Object.values(viewer.frame.parts).map(part => part.loadedFilename).join()
	stopTransition()
}

export async function stopTransition() {
	_viewer.scene.onBeforeRenderObservable.removeCallback(onBeforeRender)
	postProcess?.dispose()
	progress = 0
	_viewer.dispatchEvent(new Event('transitionComplete'))
	_isTransitioning = false
	_viewer.paused = false
}

export function isTransitioning() {
	return _isTransitioning
}

function onBeforeRender() {
	if (progress == 0) progress = 0.0001
	else progress += _viewer.engine.getDeltaTime() * (1 / Settings.cameraDuration * 0.001)
	if (progress >= 1) stopTransition()
}

export async function startTransition() {
	if (texA == null) return
	_isTransitioning = true
	if (!_viewer.scene || _viewer.frame.hasErrors) {
		stopTransition()
		return
	}
	if (filenames != Object.values(_viewer.frame.parts).map(part => part.loadedFilename).join()) {
		stopTransition()
		return
	}
	_viewer.scene.render(true)
	_viewer.paused = true
	progress = 0
	postProcess?.dispose()
	postProcess = new PostProcess(
		'blendTransition',
		'blendShader',
		['progress', 'center', 'randomSeed'],
		['blendTexture'],
		1.0,
		_viewer.camera,
		Texture.BILINEAR_SAMPLINGMODE,
		_viewer.engine,
		false // not reusable
	)
	const random = Math.random()
	postProcess.onApply = (effect) => {
		effect.setTexture('blendTexture', texA)
		effect.setFloat('progress', progress) // 0 → 1
		effect.setVector2('center', center)
		effect.setFloat('randomSeed', random)
	}
	await _viewer.scene.whenReadyAsync()
	_viewer.paused = false
	_viewer.camera.onViewMatrixChangedObservable.addOnce(stopTransition)
	_viewer.scene.onBeforeRenderObservable.add(onBeforeRender)
}
